import React, { useMemo } from 'react';
import Banner from '../../../../../components/Banner';
import IconAiArrow from '../../../../../components/IconWrapper/arrow-ai';
import usePlatform from '../../../../../hooks/usePlatform';
import { getBannerOperationExtraAttr } from '../../../../../scripts/utils';
import './index.scss';
import '../../../../../components/AIP/aip-banner-wrap.scss';

type NoteTip = { value?: string; label?: string; img?: string };

type FeatureItem = {
  title: string;
  description: string;
};

const mallSectionDiagram1 = '/images/aip/onlinemall/unified-business-model.png';
const mallSectionDiagram2 = '/images/aip/onlinemall/configurable-multi-end-matrix.png';
const mallSectionDiagram3 = '/images/aip/onlinemall/biz-finance-integration.png';
const mallSectionDiagram4 = '/images/aip/onlinemall/ecosystem-extension-framework.png';
const case01 = '/images/aip/Retail/case-01.png';
const bannerBg = '/images/aip/onlinemall/onlinemall-banner-bg.svg';

type FeatureSectionData = {
  title: string;
  subtitle: string;
  previewAlt: string;
  previewImage: string;
  items: FeatureItem[];
};

const featureSections: FeatureSectionData[] = [
  {
    title: '统一业务模型',
    subtitle: '多态商城的“逻辑内核”',
    previewAlt: '统一业务模型示意图',
    previewImage: mallSectionDiagram1,
    items: [
      {
        title: '销售订单（SO）模型',
        description:
          '适用于销售导向型商城（如B2C、B2B自营、平台商城），核心流程为“买家下单 -> 卖家履约”，内置商品、促销、订单、支付、售后完整能力。'
      },
      {
        title: '采购申请（PR）模型',
        description:
          '适用于采购导向型商城（如经销商要货、MRO采购、福利商城），核心流程为“创建请购 -> 审批 -> 生成订单”，支持复杂的审批流与结算规则。'
      },
      {
        title: '可复用的数据资产',
        description: '统一的商品、客户、库存、价格主数据模型，一次维护，全商城复用，保障数据同源与体验一致。'
      },
      {
        title: '灵活的业务规则引擎',
        description: '通过配置即可定义不同商城的会员等级、价格策略、促销活动与工作流，满足多样化业务规则。'
      }
    ]
  },
  {
    title: '可配置多端矩阵',
    subtitle: '前台的“积木装配线”',
    previewAlt: '可配置多端矩阵示意图',
    previewImage: mallSectionDiagram2,
    items: [
      {
        title: '买家端门户快速搭建',
        description:
          '基于模板与组件库，可快速配置出面向消费者、经销商、员工等不同角色的专属购物门户，实现个性化界面与体验。'
      },
      {
        title: '卖家端工作台灵活配置',
        description: '为商家或供应商提供可配置的订单管理、商品管理、售后服务等工作台，适配其运营习惯。'
      },
      {
        title: '统一运营后台集约管理',
        description:
          '在一个后台内，可同时管理多个不同业务模式商城的商品、价格、库存、订单与会员，实现集约化运营。'
      },
      {
        title: '多端体验无缝一致',
        description: '支持PC、H5、小程序等多端适配，确保用户在不同触点获得流畅、统一的交易体验。'
      }
    ]
  },
  {
    title: '业财一体集成',
    subtitle: '交易与履约的“无缝高速公路”',
    previewAlt: '业财一体集成示意图',
    previewImage: mallSectionDiagram3,
    items: [
      {
        title: '交易单据自动流转',
        description:
          '商城生成的销售订单、采购申请可自动转换为后台的销售订单、采购订单，无需人工录入，杜绝差错。'
      },
      {
        title: '库存与价格实时同步',
        description:
          '商城前台展示的库存、价格与后端ERP/供应链中台实时同步，确保信息准确，避免超卖与纠纷。'
      },
      {
        title: '履约状态全程可视',
        description: '订单的发货、物流状态由后端履约系统驱动，并实时回显至商城前台，提升客户体验。'
      },
      {
        title: '自动化财务对接',
        description: '订单、发票、收款数据与财务系统贯通，支持自动对账与结算，实现业财一体化。'
      }
    ]
  },
  {
    title: '生态化扩展框架',
    subtitle: '复杂业务的“敏捷适配器”',
    previewAlt: '生态化扩展框架示意图',
    previewImage: mallSectionDiagram4,
    items: [
      {
        title: '深度业务流程嵌入',
        description: '原生支持复杂的审批流（采购申请）、合同管理、返利政策、多级结算等企业级B2B场景。'
      },
      {
        title: '开放平台与API',
        description: '提供丰富的API与扩展点，支持与第三方营销工具、支付网关、物流系统、CRM等快速集成。'
      },
      {
        title: '多商城统一门户',
        description:
          '一个集团可基于同一套后台，为员工福利、经销商订货、闲置资产处置等不同业务线快速搭建多个独立商城，统一管理。'
      },
      {
        title: '伙伴生态协同',
        description: '可轻松构建连接品牌商、经销商与终端客户的B2B2B平台商城，实现产业链的高效协同。'
      }
    ]
  }
];

function FeatureSection({ section }: { section: FeatureSectionData }) {
  return (
    <section className="section">
      <div className="container">
        <h2 className="section-title">{section.title}</h2>
        <p className="section-subtitle feature-subtitle">{section.subtitle}</p>
        <div className="feature-split">
          <div className="feature-list">
            {section.items.map((item, index) => (
              <article key={`${section.title}-${item.title}`} className="feature-item feature-item-static">
                <div className="feature-head">
                  <span className="feature-title">{item.title}</span>
                  <span className="feature-index">#{index + 1}</span>
                </div>
                <p className="feature-desc">{item.description}</p>
              </article>
            ))}
          </div>
          <div className="feature-preview">
            <div className="feature-placeholder">
              <img
                className="feature-preview-image"
                src={section.previewImage}
                alt={section.previewAlt}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Onlinemall() {
  const { isPc } = usePlatform() || {};

  const bannerConfig = useMemo(() => {
    const src = bannerBg;

    const note =
      '通过“一套模型、多端适配”的可复用技术底座，实现“一个后台，多种商城”的集约化运营，让企业聚焦业务创新，打造无缝的数字化交易生态。';

    const noteTips: NoteTip[] = [
      { value: '+70%', label: '商城上线速度' },
      { value: '-50%', label: '多商城运营成本' },
      { value: '+40%', label: '全渠道交易效率' }
    ];

    return {
      src,
      mobileSrc: bannerBg,
      title: '十大应用-商城',
      subheading: '',
      legend: '构建统一、灵活、可扩展的数字化交易门户',
      note,
      noteTips,
      forceShowNote: true,
      operation: [
        {
          text: '立即咨询',
          icon: <IconAiArrow />,
          extraAttr: getBannerOperationExtraAttr({
            jumpBO: { formBOId: 1, formBO: { id: 1 }, jumpContentType: 'FORM' }
          })
        }
      ]
    };
  }, []);

  return (
    <div className="onlinemall-page">
      <div className="aip-banner-wrap onlinemall-banner">
        <Banner
          showAdvantage={false}
          covered
          useArrow={false}
          useDots={false}
          size="large"
          type={'BANNER_TYPE.CAROUSEL'}
          name={typeof bannerConfig.title === 'string' ? bannerConfig.title : undefined}
          height={isPc ? 700 : 600}
          config={bannerConfig as any}
        />
      </div>

      <main>
        {featureSections.map((section) => (
          <FeatureSection key={section.title} section={section} />
        ))}

        {/* 行业实践案例 - 暂时隐藏 */}
        {/* <section className="section" id="cases">
          <div className="container">
            <h2 className="section-title">行业实践案例</h2>
            <p className="section-subtitle feature-subtitle">智慧零售运营中枢实践案例</p>
            <div className="cases">
              <a className="case-link" href="https://terminus.io/case-detail/2009001" target="_blank" rel="noopener noreferrer">
                <article className="case-card">
                  <span className="case-badge">案例 01</span>
                  <div className="case-body">
                    <h3 className="case-title">智慧零售全渠道业务增长实践</h3>
                    <p className="case-desc">
                      通过一盘货、统一订单中心和客户引擎协同联动，帮助企业提升库存周转效率、履约人效与会员复购表现。
                    </p>
                    <div className="case-illustration">
                      <img className="case-illustration-img" src={case01} alt="零售案例配图" loading="lazy" />
                    </div>
                  </div>
                </article>
              </a>
            </div>
          </div>
        </section> */}
      </main>
    </div>
  );
}
